/*
 * supps-reveal.js  -  Phase 0 reveal engine (council-approved contract)
 * ONE engine, shared contract. No visible animation until elements are tagged (Phase 1).
 *
 * How Phase 1 tags an element:
 *   data-supps-anim="slide|pop|fade"   -> entrance token (default slide)
 *   data-supps-safe                    -> conversion-path / LCP: NEVER opacity-gated (default-visible, transform-only settle)
 *   data-supps-delay="120"             -> ms delay (sequencing)
 *   data-supps-stagger                 -> container; its [data-supps-stagger-child] reveal at 60ms steps (cap 6)
 *
 * Guardrails baked in: prefers-reduced-motion short-circuit (no listeners, no will-change),
 * single IntersectionObserver + rAF-batched scroll fallback (the theme scrolls a container that can
 * outrun the observer), force-reveal safety net (content/buy box can never stay hidden), will-change
 * concurrency cap, reveal-once, transform+opacity only, reserved boxes (no CLS), named brand tokens only.
 */
(function () {
  'use strict';

  var html = document.documentElement;
  var REDUCE = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;

  // Named tokens — verbatim from knowledge_base/brand_kit/tokens.json (no hand-rolled curves).
  var TOKENS = {
    snap:  { dur: 180, ease: 'cubic-bezier(.2,.7,.2,1)' },
    pop:   { dur: 600, ease: 'cubic-bezier(.2,.9,.3,1.3)' },
    slide: { dur: 700, ease: 'cubic-bezier(.2,.9,.3,1.1)' }
  };
  var FORCE_REVEAL_MS = 2600;   // safety net: anything still hidden after this is force-shown
  var WILL_CHANGE_CAP = 8;      // never promote more than 8 layers at once
  var STAGGER_MS = 60;
  var STAGGER_MAX = 6;          // beyond this, no per-child stagger (avoids loading-screen feel)

  var willChangeActive = 0;
  var io = null;
  var scrollPending = false;

  function reduced() { return !!(REDUCE && REDUCE.matches); }
  function targets() { return [].slice.call(document.querySelectorAll('[data-supps-anim]')); }
  function isSafe(el) { return el.hasAttribute('data-supps-safe'); }
  function small() { return (window.innerWidth || html.clientWidth) <= 749; }

  function injectCSS() {
    if (document.getElementById('supps-reveal-css')) return;
    var css =
      '@keyframes supps-rise{from{opacity:0;transform:translateY(30px)}to{opacity:1;transform:none}}' +
      '@keyframes supps-rise-sm{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:none}}' +
      '@keyframes supps-fade{from{opacity:0}to{opacity:1}}' +
      '@keyframes supps-pop{from{opacity:0;transform:scale(.92)}to{opacity:1;transform:none}}' +
      /* safe variants: transform-only, never gate opacity (LCP image, buy box) */
      '@keyframes supps-pop-safe{from{transform:scale(.94)}to{transform:none}}' +
      '@keyframes supps-settle{from{transform:scale(1.04)}to{transform:none}}';
    var st = document.createElement('style');
    st.id = 'supps-reveal-css';
    st.textContent = css;
    document.head.appendChild(st);
  }

  function applyHidden(el) {
    // Safe (conversion/LCP) elements are NEVER hidden — visible by default, animation is pure enhancement.
    if (isSafe(el)) { el.setAttribute('data-supps-state', 'safe'); return; }
    var t = el.getAttribute('data-supps-anim');
    el.style.setProperty('opacity', '0', 'important');
    if (t === 'pop') el.style.setProperty('transform', 'scale(.92)', 'important');
    else if (t === 'fade') { /* opacity only, no transform (no box change) */ }
    else el.style.setProperty('transform', 'translateY(' + (small() ? '20px' : '30px') + ')', 'important');
    el.setAttribute('data-supps-state', 'hidden');
  }

  function keyframeFor(el) {
    var t = el.getAttribute('data-supps-anim');
    if (isSafe(el)) return t === 'pop' ? 'supps-pop-safe' : 'supps-settle';
    if (t === 'pop') return 'supps-pop';
    if (t === 'fade') return 'supps-fade';
    return small() ? 'supps-rise-sm' : 'supps-rise';
  }
  function tokenFor(el) {
    var t = el.getAttribute('data-supps-anim');
    return t === 'pop' ? TOKENS.pop : TOKENS.slide;
  }

  function reveal(el) {
    if (el.getAttribute('data-supps-state') === 'shown') return;
    el.setAttribute('data-supps-state', 'shown');
    var tok = tokenFor(el);
    var kf = keyframeFor(el);
    var delay = parseInt(el.getAttribute('data-supps-delay') || '0', 10);
    var promote = willChangeActive < WILL_CHANGE_CAP;
    if (promote) { el.style.setProperty('will-change', 'transform, opacity'); willChangeActive++; }
    el.style.removeProperty('opacity');
    el.style.removeProperty('transform');
    el.style.setProperty('animation', kf + ' ' + tok.dur + 'ms ' + tok.ease + ' ' + delay + 'ms both');
    el.addEventListener('animationend', function done() {
      el.style.removeProperty('animation');
      if (promote) { el.style.removeProperty('will-change'); willChangeActive--; }
      el.removeEventListener('animationend', done);
    });
    if (el.hasAttribute('data-supps-stagger')) {
      var kids = [].slice.call(el.querySelectorAll('[data-supps-stagger-child]'));
      var anim = small() ? 'supps-rise-sm' : 'supps-rise';
      kids.slice(0, STAGGER_MAX).forEach(function (k, i) {
        k.style.setProperty('animation', anim + ' ' + TOKENS.slide.dur + 'ms ' + TOKENS.slide.ease + ' ' + (delay + i * STAGGER_MS) + 'ms both');
      });
    }
  }

  function sweep() {
    // Read pass first (all geometry reads), then write pass (all reveals) --
    // interleaving read/write per-element here forces a layout recalc on every
    // iteration once any element's style changes. Batching avoids that.
    var vh = window.innerHeight || html.clientHeight;
    var due = [];
    targets().forEach(function (el) {
      if (el.getAttribute('data-supps-state') !== 'shown' &&
          el.getBoundingClientRect().top < vh * 0.9) {
        due.push(el);
      }
    });
    due.forEach(function (el) {
      reveal(el);
      if (io) io.unobserve(el);
    });
  }
  function onScroll() {
    if (scrollPending) return;
    scrollPending = true;
    requestAnimationFrame(function () { sweep(); scrollPending = false; });
  }
  function forceRevealAll() {
    targets().forEach(function (el) { if (el.getAttribute('data-supps-state') !== 'shown') reveal(el); });
  }

  function init() {
    var t = targets();
    if (!t.length) return;            // inert until Phase 1 tags elements
    injectCSS();
    if (reduced()) {                  // reduced-motion: everything visible, no listeners, no will-change
      t.forEach(function (el) { el.setAttribute('data-supps-state', 'shown'); });
      return;
    }
    html.classList.add('supps-anim-ready');
    t.forEach(applyHidden);
    io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) { if (e.isIntersecting) { reveal(e.target); io.unobserve(e.target); } });
    }, { threshold: 0.12, rootMargin: '0px 0px -7% 0px' });
    t.forEach(function (el) { io.observe(el); });
    window.addEventListener('scroll', onScroll, true);  // capture: catch container scroll
    window.addEventListener('resize', onScroll);
    // rAF, not a direct call: applyHidden() above just wrote styles to every
    // target, so reading getBoundingClientRect() synchronously here would force
    // an immediate layout recalc. Deferring one frame lets the browser's own
    // layout/paint pass happen first instead of us forcing it.
    requestAnimationFrame(sweep);                       // reveal above-the-fold immediately
    setTimeout(forceRevealAll, FORCE_REVEAL_MS);        // safety net
    if (REDUCE && REDUCE.addEventListener) {
      REDUCE.addEventListener('change', function (e) { if (e.matches) forceRevealAll(); });
    }
  }

  // Debug / Phase-1 verification handle.
  window.SuppsReveal = { init: init, reveal: reveal, sweep: sweep, tokens: TOKENS, version: '1.0-phase0' };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
